'use client'

import { ChartContainer } from '@/components/ui/chart'
import { customPercentileLabel } from '@/modules/salary/common/utils'
import type { SalaryHistogramEntry } from '@/modules/salary/types'
import { formatAbbreviatedDollar } from '@/utils/currency-formatters'
import { useEffect, useState } from 'react'
import { Bar, BarChart, type BarProps, XAxis } from 'recharts'
import { cn } from '@/lib/css-utils'
import { motion } from 'framer-motion'

type CustomBarProps = Partial<BarProps> & {
  count?: number
  salary?: number
  setSelectedBar?: (bar: CustomBarProps | null) => void
  hideTooltip?: boolean
  animationDelay?: number
  showHistogramAnimation?: boolean
}

const CustomBar = (props: CustomBarProps) => {
  const {
    x,
    y,
    width = 0,
    height = 0,
    animationDelay = 0,
    hideTooltip,
    setSelectedBar,
    showHistogramAnimation,
  } = props

  return (
    <g
      onMouseEnter={() => !hideTooltip && setSelectedBar?.(props)}
      onMouseLeave={() => !hideTooltip && setSelectedBar?.(null)}
    >
      <motion.rect
        x={Number(x)}
        y={Number(y)}
        width={width}
        height={height}
        rx={4}
        ry={4}
        className="fill-chart-bar-fill hover:fill-chart-bar-highlight transition-colors"
        initial={{ scaleY: showHistogramAnimation ? 0 : 1, originY: 1 }}
        animate={{ scaleY: 1 }}
        transition={
          showHistogramAnimation
            ? {
                duration: 0.3,
                ease: 'easeOut',
                delay: animationDelay / 7000,
              }
            : undefined
        }
      />
    </g>
  )
}

function HistogramTooltip({ width, x, y, salary, count }: CustomBarProps) {
  const _x = Number(x)
  const _y = Number(y)
  const _width = Number(width)

  return (
    <div
      className="absolute bg-white z-20 rounded-lg"
      style={{
        // width var is the bar width, and we remove 48px that is half of the tooltip div width
        left: `${_x + _width / 2 - 50}px`,
        // 55px to leave a small space between the bar and the tooltip to add the vertical line
        top: `${_y - 55}px`,
      }}
    >
      <div
        className={cn(
          'bg-white w-[100px] h-[45px] rounded-lg p-1 flex flex-col shadow-md',
          'text-sm font-medium items-center border border-background-muted',
        )}
      >
        <span className="text-copy-accent dark:text-copy-pale">
          {formatAbbreviatedDollar(salary ?? 0)}
        </span>
        <span className="text-copy-muted dark:text-copy-pale text-xs">
          {`${count} ${count === 1 ? 'Salary' : 'Salaries'}`}
        </span>
      </div>
      <div className="absolute left-1/2 -translate-x-1/2 top-full h-[10px] w-[1px] bg-copy-muted" />
    </div>
  )
}

const defaultData = [
  { percentile: '10th', salary: 100000, count: 1 },
  { percentile: '25th', salary: 200000, count: 1 },
  { percentile: '50th', salary: 300000, count: 1 },
  { percentile: '75th', salary: 400000, count: 1 },
  { percentile: '90th', salary: 500000, count: 1 },
]

interface SalaryHistogramProps {
  data: SalaryHistogramEntry[]
  blurAmounts?: boolean
  isLowData?: boolean
}

const ANIMATION_DELAY_INTERVAL = 200

export default function HistogramChart({
  data,
  isLowData,
  blurAmounts,
}: SalaryHistogramProps) {
  const [showHistogramAnimation, setShowHistogramAnimation] = useState(true)
  const [selectedBar, setSelectedBar] = useState<CustomBarProps | null>(null)

  const collection = (data.length ? data : defaultData).map((entry, index) => ({
    ...entry,
    hideTooltip: blurAmounts || isLowData,
    animationDelay: index * ANIMATION_DELAY_INTERVAL,
    setSelectedBar,
  }))

  useEffect(() => {
    setShowHistogramAnimation(true)
    setTimeout(() => {
      setShowHistogramAnimation(false)
      // animation takes 0.3s, so after 1s we disable the animation.
    }, 1000)
  }, [data])

  return (
    <>
      {selectedBar && !blurAmounts ? (
        <HistogramTooltip {...selectedBar} />
      ) : null}
      <ChartContainer config={{}} className="h-full w-full">
        <BarChart
          data={collection}
          accessibilityLayer
          margin={{ top: 20, bottom: 10 }}
        >
          <XAxis
            dataKey="salary"
            axisLine={false}
            tickLine={false}
            interval={0}
            className="text-xs text-copy-accent font-medium w-full"
            tick={(tickProps) => {
              const { index } = tickProps

              const dataLength = data.length.toString()
              const label = customPercentileLabel.get(dataLength)?.[index]

              if (!label) {
                return <></>
              }

              return (
                <g transform={`translate(${tickProps.x},${tickProps.y})`}>
                  {/* Render the main text */}
                  <text
                    x={0}
                    y={0}
                    dy={10}
                    textAnchor="middle"
                    className="fill-copy-primary"
                    filter={blurAmounts ? 'url(#blur-filter)' : undefined}
                  >
                    {formatAbbreviatedDollar(tickProps.payload.value as number)}
                  </text>
                  {/* Render the secondary label */}
                  <text
                    x={0}
                    y={10}
                    dy={16}
                    textAnchor="middle"
                    className="fill-copy-accent"
                    fontWeight="normal"
                  >
                    {label || index}
                  </text>
                </g>
              )
            }}
          />
          <Bar
            dataKey="count"
            shape={
              <CustomBar showHistogramAnimation={showHistogramAnimation} />
            }
          />
          <defs>
            <style type="text/css">
              {'.recharts-bar-rectangle { transition: fill 0.3s ease; }'}
            </style>
            <filter id="blur-filter" x="0" y="0">
              <feGaussianBlur in="SourceGraphic" stdDeviation="3" />
            </filter>
          </defs>
        </BarChart>
      </ChartContainer>
    </>
  )
}
